import { mapImage } from "@/utils/imageHelper";

import listItem from "@/types/list";
import { motion } from "framer-motion";
import { StrapiImage } from "@/types/strapi";
import * as Icons from "lucide-react";
import { LucideIcon } from "lucide-react";

interface BenefitsProps {
	benefits: listItem[];
	title: string
	subTitle?: string
	image: StrapiImage
}

export default function Benefits({ benefits, title, subTitle, image }: BenefitsProps) {
	const mappedHeroImage = mapImage(image as StrapiImage, true);

	const renderIcon = (iconName: string): React.ReactElement => {

		const IconComponent = Icons[iconName as keyof typeof Icons] as LucideIcon | undefined;
		if (IconComponent) {
			return <IconComponent className="text-white" />;
		}
		return <Icons.Star className="text-white" />;
	}

	return (
		<section id="benefits" className="py-20 bg-white">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-8">
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8 }}
					viewport={{ once: true }}
					className="text-center mb-16"
				>
					<h2 className="font-normal text-black font-abc-prophet text-4xl leading-tight lg:text-[58px] lg:leading-[76px] mb-6">
						{title}
					</h2>
					{
						subTitle && (
							<p className="text-xl lg:text-[24px] text-black font-karla">
								{subTitle}
							</p>
						)
					}
				</motion.div>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
					{/* Left Column - Benefits List */}
					<motion.div
						initial={{ opacity: 0, x: 30 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8, delay: 0.2 }}
						viewport={{ once: true }}
						className="flex justify-center"
					>
						{image && (
							<img
								src={mappedHeroImage?.url}
								alt={mappedHeroImage?.alternativeText || "Benefits Image"}
								className="w-full h-auto max-w-lg object-cover xl:aspect-[4/5]"
							/>
						)}
					</motion.div>

					{/* Right Column - Trust Card */}
					<motion.ul
						initial={{ opacity: 0, x: -30 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.8 }}
						viewport={{ once: true }}
						className="space-y-8"
					>
						{benefits && benefits.map((benefit, index) => (
							<motion.li
								key={benefit.id}
								initial={{ opacity: 0, x: -30 }}
								whileInView={{ opacity: 1, x: 0 }}
								transition={{ duration: 0.8, delay: index * 0.1 }}
								viewport={{ once: true }}
								className="flex items-start space-x-6"
							>
								{
									benefit.listMedia == "icon" && benefit.icon && (
										<i className="w-12 h-12 flex-shrink-0 bg-newTheme-orange rounded-full flex items-center justify-center">
											{renderIcon(benefit.icon as string)}
										</i>
									)
								}
								{
									benefit.listMedia == "image" && benefit.listImage && (
										benefit.listImage && (() => {
											const img = mapImage(benefit.listImage as StrapiImage, false);
											return img ? (
												<img
													src={img.url}
													alt={img.alternativeText || benefit.title || ""}
													className="w-auto h-8 mx-auto object-cover flex-shrink-1"
												/>
											) : null;
										})()
									)
								}
								<div className="flex-grow">
									{
										benefit.title && (
											<h3 className="text-lg lg:text-[32px] lg:leading-[46px] font-normal text-black font-abc-prophet mb-2">
												{benefit.title}
											</h3>
										)
									}
									<p className="text-black font-karla leading-relaxed lg:text-xl lg:leading-8">
										{benefit.content}
									</p>
								</div>
							</motion.li>
						))}
					</motion.ul>
				</div>
			</div>
		</section>
	)
}